/**
 * SnapAdLandingPage — iter112
 * ────────────────────────────────────────────────────────────
 * Vertical (9:16-optimised) landing page tuned for Snapchat /
 * TikTok / Instagram Story ads. Visitors arriving from an ad
 * see the pricing story instantly with a single gigantic CTA
 * to reach the live platform.
 *
 * Route: /snap  (short shareable URL — no dashes, memorable)
 *
 * Design principles:
 *   • Full-viewport phone-first layout — no wasted space.
 *   • ONE loud CTA above the fold + a floating repeat CTA.
 *   • Vertically stacked plan cards (thumb-friendly).
 *   • Bilingual toggle at top-right so ads can target both.
 *   • Zero form fields — pure "look → tap → go".
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FEATURES } from "../lib/features";
import {
  Sparkles, ShieldCheck, Zap, ArrowRight, Rocket, Crown, Gift,
} from "lucide-react";
import { useI18n } from "../lib/i18n";
// iter242 — launchPrice import removed: the /snap landing is now
// price-less (free offer only) so the discount helper is unused here.
import { trackEvent } from "../lib/ga4";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_carvo-market/artifacts/c5durw9k_B010DD54-BD83-49AB-A5A3-946EA832B9AF.png";
const LIVE_URL = "https://cartal.sa";

const PLANS = [
  {
    id: "basic",
    icon: Sparkles,
    bg: "linear-gradient(135deg,#0a0a0a 0%,#141010 100%)",
    accent: "#c9a55c",
    tag_ar: "ابدأ الآن", tag_en: "GET STARTED",
    name_ar: "أساسي مجاناً", name_en: "Basic — FREE",
    desc_ar: "ابدأ البيع فوراً بلا رسوم", desc_en: "Start selling instantly — no fees",
    // iter242 — free-listing count kept in sync with the recent
    // pricing update (Basic = 5 free listings). If that number moves
    // in the backend catalog, update ONLY this constant.
    freeListings: 5,
  },
];

export default function SnapAdLandingPage() {
  const { lang, setLang } = useI18n();
  const ar = lang === "ar";
  const dir = ar ? "rtl" : "ltr";

  // Pre-warm the primary CTA click event for GA4 attribution.
  const go = (source) => trackEvent("snap_ad_cta_click", { source });

  return (
    <div
      dir={dir}
      className="min-h-screen w-full pb-28"
      style={{
        background: "radial-gradient(circle at 50% 20%, #1a1208 0%, #0a0806 60%, #050505 100%)",
        color: "#f7eecd",
      }}
      data-testid="snap-ad-page"
    >
      {/* Top bar — language toggle */}
      <div className="flex items-center justify-between px-4 pt-4">
        <img src={LOGO_URL} alt="CARTAL" className="h-9 object-contain" />
        <button
          type="button"
          onClick={() => setLang(ar ? "en" : "ar")}
          className="rounded-full px-3 py-1 text-[11px] font-black border"
          style={{ borderColor: "#c9a55c", color: "#f5d478", background: "rgba(201,165,92,0.08)" }}
          data-testid="snap-lang-toggle"
        >
          🌐 {ar ? "English" : "العربية"}
        </button>
      </div>

      {/* Hero */}
      <div className="text-center px-5 mt-6">
        <div
          className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] font-black tracking-[0.25em] mb-3"
          style={{ background: "rgba(239,68,68,0.15)", color: "#fecaca", border: "1px solid rgba(239,68,68,0.5)" }}
        >
          <Rocket className="w-3 h-3" />
          <span>{ar ? "عرض إطلاق محدود · خصم 50٪" : "LIMITED LAUNCH · 50% OFF"}</span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black leading-tight" style={{ color: "#f5d478" }}>
          {ar ? "قِد أكثر." : "Drive more."}
        </h1>
        <h1 className="font-display text-4xl sm:text-5xl font-black leading-tight mt-1" style={{ color: "#f5d478" }}>
          {ar ? "عِش أفضل." : "Live better."}
        </h1>
        <p className="mt-3 text-sm max-w-xs mx-auto leading-relaxed" style={{ color: "rgba(247,238,205,0.8)" }}>
          {FEATURES.ESCROW_ENABLED
            ? (ar
                ? "بيع، اشترِ، وموّل السيارات في منصة واحدة — مع ضمان كارتال الحصري."
                : "Buy, sell & finance vehicles on one platform — with the exclusive CARTAL Guarantee.")
            : (ar
                ? "بيع، اشترِ، وموّل السيارات في منصة واحدة بأمان وسرعة."
                : "Buy, sell & finance vehicles on one platform — fast and secure.")}
        </p>
      </div>

      {/* Primary CTA — above the fold */}
      <div className="px-5 mt-6">
        <a
          href={LIVE_URL}
          onClick={() => go("hero")}
          target="_blank" rel="noopener noreferrer"
          className="relative flex items-center justify-center gap-2 w-full py-4 rounded-full font-display text-xl font-black transition-transform active:scale-[0.98]"
          style={{
            background: "linear-gradient(180deg,#f5d478 0%,#c9a55c 55%,#8a6f2e 100%)",
            color: "#0a0806",
            border: "2px solid #f5d478",
            boxShadow: "0 12px 32px -8px rgba(245,212,120,0.5), inset 0 1px 0 rgba(255,255,255,0.4)",
          }}
          data-testid="snap-cta-hero"
        >
          <Sparkles className="w-5 h-5" />
          <span dir="ltr">cartal.sa</span>
          <ArrowRight className={`w-5 h-5 ${ar ? "rotate-180" : ""}`} />
        </a>
        <div className="text-center text-[10px] tracking-[0.25em] font-bold mt-2" style={{ color: "#c9a55c" }}>
          {ar ? "المنصة السعودية الرسمية" : "OFFICIAL SAUDI PLATFORM"}
        </div>
      </div>

      {/* iter242 — Free offer showcase (prices removed per request).
          Was a 3-plan comparison; now surfaces the FREE tier only with
          the current free-listing count from the recent pricing update. */}
      <div className="px-5 mt-8">
        <div className="text-center mb-4">
          <div className="text-[10px] tracking-[0.3em] font-bold" style={{ color: "#c9a55c" }}>
            {ar ? "عرض مجاني" : "FREE OFFER"}
          </div>
          <h2 className="font-display text-2xl font-black mt-1" style={{ color: "#f5d478" }}>
            {ar ? "ابدأ البيع مجاناً" : "Start selling for free"}
          </h2>
        </div>

        <div className="flex flex-col gap-3">
          {PLANS.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.id}
                className="relative rounded-2xl p-4"
                style={{
                  background: p.bg,
                  border: `1.5px solid ${p.accent}55`,
                  boxShadow: `0 10px 30px -12px ${p.accent}55`,
                }}
                data-testid={`snap-plan-${p.id}`}
              >
                {/* Corner tag */}
                <div
                  className="absolute -top-2 start-3 rounded-md px-2 py-0.5 text-[9px] font-black uppercase tracking-wider"
                  style={{ background: p.accent, color: "#0a0806" }}
                >
                  {ar ? p.tag_ar : p.tag_en}
                </div>

                <div className="flex items-center gap-3 mt-2">
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: `${p.accent}20`, border: `1.5px solid ${p.accent}` }}
                  >
                    <Icon className="w-5 h-5" style={{ color: p.accent }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg font-black truncate" style={{ color: "#f7eecd" }}>
                      {ar ? p.name_ar : p.name_en}
                    </div>
                    <div className="text-[11px] leading-snug" style={{ color: "rgba(247,238,205,0.7)" }}>
                      {ar ? p.desc_ar : p.desc_en}
                    </div>
                  </div>
                  <div className="text-end shrink-0">
                    <div className="font-display text-2xl font-black" style={{ color: p.accent }}>
                      {ar ? "مجاناً" : "FREE"}
                    </div>
                    <div
                      className="text-[10px] font-bold mt-0.5"
                      style={{ color: "rgba(247,238,205,0.85)" }}
                      data-testid={`snap-plan-${p.id}-freelistings`}
                    >
                      {ar
                        ? `${p.freeListings} إعلانات مجانية`
                        : `${p.freeListings} free listings`}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Trust strip */}
      <div className="px-5 mt-8">
        <div
          className="rounded-2xl p-4 grid grid-cols-3 gap-3"
          style={{
            background: "linear-gradient(180deg,rgba(201,165,92,0.08),rgba(201,165,92,0.02))",
            border: "1px solid rgba(201,165,92,0.35)",
          }}
        >
          {[
            FEATURES.ESCROW_ENABLED && { icon: ShieldCheck, ar: "ضمان كارتال", en: "GUARANTEE" },
            { icon: Zap,         ar: "دفع فوري",     en: "INSTANT" },
            { icon: Crown,       ar: "موثوق ٢٤/٧",   en: "TRUSTED 24/7" },
          ].filter(Boolean).map((t, i) => {
            const Icon = t.icon;
            return (
              <div key={i} className="flex flex-col items-center text-center gap-1">
                <div className="w-10 h-10 rounded-full flex items-center justify-center"
                     style={{ background: "rgba(201,165,92,0.15)", border: "1px solid rgba(201,165,92,0.4)" }}>
                  <Icon className="w-4 h-4" style={{ color: "#f5d478" }} />
                </div>
                <div className="text-[10px] font-black tracking-wider" style={{ color: "#f5d478" }}>
                  {ar ? t.ar : t.en}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Secondary CTA — end of scroll */}
      <div className="px-5 mt-8">
        <a
          href={LIVE_URL}
          onClick={() => go("footer")}
          target="_blank" rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full py-4 rounded-full font-display text-lg font-black transition-transform active:scale-[0.98]"
          style={{
            background: "linear-gradient(180deg,#f5d478 0%,#c9a55c 55%,#8a6f2e 100%)",
            color: "#0a0806",
            border: "2px solid #f5d478",
          }}
          data-testid="snap-cta-footer"
        >
          {ar ? "ابدأ الآن على" : "Start now at"} <span dir="ltr">cartal.sa</span>
          <ArrowRight className={`w-5 h-5 ${ar ? "rotate-180" : ""}`} />
        </a>
        <div className="text-center text-[10px] mt-3 opacity-60">
          <Link to="/gift/plans" className="hover:opacity-100" style={{ color: "#c9a55c" }}>
            <Gift className="inline w-3 h-3 mx-1" />
            {ar ? "شارك الرابط كهدية" : "Share as a gift"}
          </Link>
        </div>
      </div>

      {/* Floating persistent CTA — always visible at bottom */}
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 px-4 w-full max-w-md"
        style={{ pointerEvents: "none" }}
      >
        <a
          href={LIVE_URL}
          onClick={() => go("sticky")}
          target="_blank" rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 py-3 rounded-full font-display text-base font-black shadow-2xl transition-transform active:scale-[0.98]"
          style={{
            background: "linear-gradient(180deg,#f5d478 0%,#c9a55c 55%,#8a6f2e 100%)",
            color: "#0a0806",
            border: "2px solid #f5d478",
            pointerEvents: "auto",
            boxShadow: "0 18px 40px -8px rgba(245,212,120,0.6)",
          }}
          data-testid="snap-cta-sticky"
        >
          <Sparkles className="w-4 h-4" />
          {ar ? "اذهب إلى" : "Go to"} <span dir="ltr">cartal.sa</span>
        </a>
      </div>
    </div>
  );
}
